import { getV3Inventories } from 'gen/endpoints/inventory'
import type {
  GetV3Inventories200,
  GetV3InventoriesParams,
  InventoryListItem,
} from 'gen/models'
import useSWRInfinite, { SWRInfiniteKeyLoader } from 'swr/infinite'

export type UseInventoriesOptions = {
  searchParams?: Record<string, unknown>
  limit?: number
  ownerId?: string
  availableOnly?: boolean
}

export type UseInventoriesResult = {
  inventories: InventoryListItem[]
  totalCount: number
  hasMore: boolean
  isLoading: boolean
  isLoadingMore: boolean
  isValidating: boolean
  error: unknown
  setSize: (
    size: number | ((_size: number) => number),
  ) => Promise<GetV3Inventories200[] | undefined>
  size: number
  mutate: () => Promise<GetV3Inventories200[] | undefined>
}

export const useInventories = ({
  searchParams,
  limit = 20,
  ownerId,
  availableOnly,
}: UseInventoriesOptions = {}): UseInventoriesResult => {
  const getKey: SWRInfiniteKeyLoader<GetV3Inventories200> = (
    pageIndex,
    previousPageData,
  ) => {
    // If we've reached the end, stop fetching
    if (previousPageData && previousPageData.data.length === 0) return null

    // If there's no more pages based on pagination info
    if (
      previousPageData &&
      previousPageData.pagination.page * previousPageData.pagination.limit >=
        previousPageData.pagination.totalCount
    ) {
      return null
    }

    return {
      page: pageIndex + 1,
      limit,
      ...(ownerId && { owner_id: ownerId }),
      ...(availableOnly && { available_only: 'true' }),
      ...(searchParams && { search_params: searchParams }),
    } as GetV3InventoriesParams
  }

  const { data, error, isLoading, isValidating, mutate, setSize, size } =
    useSWRInfinite(getKey, getV3Inventories, {
      initialSize: 1,
      revalidateOnFocus: false,
      revalidateFirstPage: false,
    })

  const inventories = data?.flatMap((page) => page.data) ?? []

  const totalCount = data?.[0]?.pagination.totalCount ?? 0

  const hasMore =
    data && data[data.length - 1]
      ? data[data.length - 1].pagination.page *
          data[data.length - 1].pagination.limit <
        data[data.length - 1].pagination.totalCount
      : false

  const isLoadingMore =
    isLoading ||
    (size > 0 && data && typeof data[size - 1] === 'undefined') ||
    false

  return {
    inventories,
    totalCount,
    hasMore,
    isLoading: isLoading ?? false,
    isLoadingMore,
    isValidating,
    error,
    setSize,
    size,
    mutate,
  }
}
